"use client";

import { useEffect, useRef } from "react";
import "./globals.css";
import { reportClientErrorToSupport } from "@/lib/client-error-report";

/**
 * Root-level fallback when the root layout fails. Must define html/body.
 */
export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const sent = useRef(false);

  useEffect(() => {
    if (sent.current) return;
    sent.current = true;
    void reportClientErrorToSupport({
      kind: "global_error",
      message: error.message || "Unknown root error",
      stack: error.stack,
      digest: error.digest,
    });
  }, [error]);

  return (
    <html lang="en">
      <body className="min-h-screen bg-background font-sans text-foreground antialiased">
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6">
          <h1 className="text-xl font-semibold">Something went wrong</h1>
          <p className="max-w-md text-center text-sm text-muted-foreground">
            A critical error occurred. We emailed our team a report with details. Please try reloading the page.
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              onClick={() => reset()}
            >
              Try again
            </button>
            <button
              type="button"
              className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium hover:bg-accent"
              onClick={() => {
                if (typeof window === "undefined") return;
                try {
                  const ref = document.referrer;
                  const sameOriginRef = ref && new URL(ref).origin === window.location.origin;
                  if (window.history.length > 1 && sameOriginRef) {
                    window.history.back();
                    return;
                  }
                } catch {
                  /* fall through */
                }
                window.location.href = "/dashboard";
              }}
            >
              Go back
            </button>
          </div>
        </div>
      </body>
    </html>
  );
}
